
import { defineComponent, reactive, ref, toRefs, watch } from 'vue'
import { gotoDetail } from '@/utils/stock'
export default defineComponent({
  props: {
    value: {
      type: String,
      default: () => {
        return ''
      }
    },
    theme: {
      type: String,
      default: () => {
        return 'light'
      }
    },
    contenteditable: {
      type: Boolean,
      default: () => {
        return true
      }
    },
    disableHuShenStock: {
      type: Boolean,
      default: false
    }
  },
  setup(props: any, context) {
    const pageData = reactive({
      content: '',
      isBlur: true,
      key: Date.now(),
      isHovered: false
    })

    // 判断是否是沪深股票
    function isHuShenStock(marketCode: string) {
      const code = (marketCode || '').toLowerCase()
      return code === 'sh' || code === 'sz'
    }

    // 记录上一次的 value，用于检测是否是笔记切换（内容完全不同）
    let lastValue = ''
    // 标记是否是用户正在编辑（用于防止 watch 触发重新渲染导致光标丢失）
    let isUserEditing = false

    watch(
      () => props.value,
      (val: string) => {
        if (typeof val === 'string') {
          // 如果是用户正在编辑，不更新内容（防止光标丢失）
          if (isUserEditing) {
            lastValue = val
            return
          }

          // 检测是否是笔记切换：内容完全不同（排除用户正在编辑删除内容的情况）
          const isNoteSwitch = lastValue !== val && (
            // 从空变为有内容（新笔记加载）
            (!lastValue && val) ||
            // 内容长度变化很大且不是逐步删除
            (Math.abs(lastValue.length - val.length) > 50) ||
            // 内容完全不同（切换笔记）
            (lastValue && val && lastValue.length > 20 && val.length > 20 &&
              !val.includes(lastValue.substring(0, 20)) && !lastValue.includes(val.substring(0, 20)))
          )

          // 如果是笔记切换，无论是否有焦点都要更新内容
          if (isNoteSwitch) {
            pageData.content = handlecontent(val)
            pageData.key = Date.now()
          } else if (pageData.isBlur) {
            // 如果只是普通编辑且编辑器失去焦点，更新内容但不更新 key（避免重新渲染）
            pageData.content = handlecontent(val)
          }

          lastValue = val
        }
      },
      {
        immediate: true
      }
    )

    // 监听 disableHuShenStock 变化，重新处理内容
    watch(
      () => props.disableHuShenStock,
      () => {
        if (props.value) {
          pageData.content = handlecontent(props.value)
          pageData.key = Date.now()
        }
      }
    )

    function handleChange(val: any) {
      pageData.content = handlecontent(pageData.content + `$-${val.Name}(${val.Code}.${val.MarketCode})-$`)
    }

    function handlecontent(content: string) {
      let str = content
      // 使用正则匹配股票标签，判断是否需要禁用
      // 格式：$-名称(代码.市场)-$
      // 先匹配整个 $-...-$ 块，然后解析最后一个括号中的代码和市场
      str = str.replace(/\$-(.+?)-\$/g, (match, inner) => {
        // 找到最后一个 (xxx.xxx) 格式的括号
        const lastParenMatch = inner.match(/^(.*)\(([^.()]+)\.([^()]+)\)$/)
        if (lastParenMatch) {
          const name = lastParenMatch[1]
          const code = lastParenMatch[2]
          const market = lastParenMatch[3]
          const disabled = props.disableHuShenStock && isHuShenStock(market)
          const className = disabled ? 'contentstock disabled' : 'contentstock'
          return `<span class="${className}" contenteditable="false">${name}(${code}.${market})</span>&nbsp;`
        }
        // 如果不匹配股票格式，保持原样
        return match
      })
      return str
    }

    const divedit = ref('')

    function getTextLength(el: HTMLElement): number {
      return (el.innerText || el.textContent || '').length
    }

    function inputcontent(e: any) {
      // 标记用户正在编辑
      isUserEditing = true

      const el = e.target as HTMLElement
      const content = el.innerHTML

      // 当内容被完全删除时，确保编辑器保持焦点
      if (!content || content === '<br>' || content.trim() === '') {
        // 清空内容但保持光标
        el.innerHTML = ''
        // 确保光标在编辑器内
        const selection = window.getSelection()
        if (selection) {
          const range = document.createRange()
          range.selectNodeContents(el)
          range.collapse(true) // 光标移到开头
          selection.removeAllRanges()
          selection.addRange(range)
        }
      }

      context.emit('input', el.innerHTML)

      // 延迟重置编辑标记，确保 watch 不会在本次事件循环中触发重新渲染
      setTimeout(() => {
        isUserEditing = false
      }, 0)
    }

    function stockclick(item: any) {
      const target = item.target
      // 如果是禁用状态，不跳转
      if (target.classList && target.classList.contains('disabled')) {
        return
      }
      const currenstock = target.innerText
      if (target.className && target.className.includes('contentstock')) {
        const data = {
          Exch: currenstock.split('(')[1].split(')')[0].split('.')[1],
          Inst: currenstock.split('(')[1].split(')')[0].split('.')[0]
        }
        gotoDetail(data)
      }
    }

    function handlePaste(e: ClipboardEvent) {
      e.preventDefault()
      const clipboardData = e.clipboardData
      if (!clipboardData) return

      if (clipboardData.files && clipboardData.files.length > 0) {
        return
      }

      const text = clipboardData.getData('text/plain')
      if (text) {
        // 标记用户正在编辑，防止 watch 触发重新渲染导致光标丢失
        isUserEditing = true

        const el = e.target as HTMLElement
        const selection = window.getSelection()

        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          // 删除选中的内容（如果有）
          range.deleteContents()
          // 创建文本节点并插入
          const textNode = document.createTextNode(text)
          range.insertNode(textNode)
          // 将光标移动到插入文本的末尾
          range.setStartAfter(textNode)
          range.setEndAfter(textNode)
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // 如果没有选区，追加到末尾
          el.appendChild(document.createTextNode(text))
        }

        context.emit('input', el.innerHTML)

        // 延迟重置编辑标记
        setTimeout(() => {
          isUserEditing = false
        }, 0)
      }
    }

    return {
      ...toRefs(pageData),
      handleChange,
      handlecontent,
      inputcontent,
      stockclick,
      handlePaste
    }
  }
})
